import { InfoCircleOutlined } from '@ant-design/icons';
import { Col, Row, Tooltip } from 'antd';
import { ColumnsType } from 'antd/lib/table/Table';
import { APPLICATION_CONFIRM_RESULT, getMoment } from 'constants/GlobalConfig';
import { ScoringStatusTag } from '../common/ScoringStatusTag';
import { ProgressStatusTag } from '../common/ProgressStatusTag';

// antd の Tree data の表示を使うための型
type DataSource = AppStatusModal.ApplicationView['users'][number];
const moment = getMoment();

export const createColumns = (
    handleShowStudentView: (name: string, uuid: string) => void,
    handleShowHistory: (breadcrumb: AppStatusModal.Breadcrumb, uuid: string) => void,
    breadcrumb: AppStatusModal.Breadcrumb,
    application: App.ForAdmin | App.ForManager | undefined,
    filterOptions: string[]
): ColumnsType<DataSource> => {
    return [
        {
            title: '氏名',
            fixed: 'left',
            width: 300,
            render: (_: any, record: any) => {
                const hasScore =
                    record?.lineItems?.some((lineItem: any) => lineItem.score_line_items?.length !== 0) ?? false;
                return (
                    <Row>
                        <Col span={18}>
                            <Tooltip mouseEnterDelay={1} title='生徒ごとのビューを開く'>
                                <button
                                    className='text-button'
                                    onClick={() => {
                                        handleShowStudentView(record.name, record.uuid);
                                    }}>
                                    {record.name}
                                </button>
                            </Tooltip>
                        </Col>
                        {hasScore && (
                            <Col span={6}>
                                <button
                                    className='text-button'
                                    onClick={() => {
                                        handleShowHistory(breadcrumb, record.uuid);
                                    }}>
                                    履歴
                                </button>
                            </Col>
                        )}
                    </Row>
                );
            },
        },
        {
            colSpan: 0,
            children: generateLineItem(application, filterOptions),
        },
    ];
};

const generateLineItem = (application: App.ForAdmin | App.ForManager | undefined, filterOptions: string[]) => {
    if (application?.line_items && application.line_items.length === 0) {
        return [
            {
                colSpan: 0,
                children: [
                    {
                        title: (
                            <>
                                <span style={{ marginRight: '0.5em' }}>
                                    {APPLICATION_CONFIRM_RESULT.SCORE_GIVEN_AND_SCORE_MAXIMUM.TITLE}
                                </span>
                                <Tooltip title='複数回解答した場合、最新の得点・満点が表示されます'>
                                    <InfoCircleOutlined />
                                </Tooltip>
                            </>
                        ),
                        dataIndex: APPLICATION_CONFIRM_RESULT.SCORE_GIVEN_AND_SCORE_MAXIMUM.KEY,
                        hidden: filterOptions.some(
                            (item) => item === APPLICATION_CONFIRM_RESULT.SCORE_GIVEN_AND_SCORE_MAXIMUM.KEY
                        ),
                        render: () => <></>,
                    },
                    {
                        title: APPLICATION_CONFIRM_RESULT.ACTIVITY_PROGRESS.TITLE,
                        dataIndex: APPLICATION_CONFIRM_RESULT.ACTIVITY_PROGRESS.KEY,
                        hidden: filterOptions.some((item) => item === APPLICATION_CONFIRM_RESULT.ACTIVITY_PROGRESS.KEY),
                        render: () => <></>,
                    },
                    {
                        title: APPLICATION_CONFIRM_RESULT.GRADING_PROGRESS.TITLE,
                        dataIndex: APPLICATION_CONFIRM_RESULT.GRADING_PROGRESS.KEY,
                        hidden: filterOptions.some((item) => item === APPLICATION_CONFIRM_RESULT.GRADING_PROGRESS.KEY),
                        render: () => <></>,
                    },
                    {
                        title: APPLICATION_CONFIRM_RESULT.TIMESTAMP.TITLE,
                        dataIndex: APPLICATION_CONFIRM_RESULT.TIMESTAMP.KEY,
                        hidden: filterOptions.some((item) => item === APPLICATION_CONFIRM_RESULT.TIMESTAMP.KEY),
                        render: () => <></>,
                    },
                    {
                        title: APPLICATION_CONFIRM_RESULT.COMMENT.TITLE,
                        dataIndex: APPLICATION_CONFIRM_RESULT.COMMENT.KEY,
                        hidden: filterOptions.some((item) => item === APPLICATION_CONFIRM_RESULT.COMMENT.KEY),
                        render: () => <></>,
                    },
                    {
                        title: APPLICATION_CONFIRM_RESULT.SCORING_USER_ID.TITLE,
                        dataIndex: APPLICATION_CONFIRM_RESULT.SCORING_USER_ID.KEY,
                        hidden: filterOptions.some((item) => item === APPLICATION_CONFIRM_RESULT.SCORING_USER_ID.KEY),
                        render: () => <></>,
                    },
                    {
                        title: APPLICATION_CONFIRM_RESULT.STARTED_AT.TITLE,
                        dataIndex: APPLICATION_CONFIRM_RESULT.STARTED_AT.KEY,
                        hidden: filterOptions.some((item) => item === APPLICATION_CONFIRM_RESULT.STARTED_AT.KEY),
                        render: () => <></>,
                    },
                    {
                        title: APPLICATION_CONFIRM_RESULT.SUBMITTED_AT.TITLE,
                        dataIndex: APPLICATION_CONFIRM_RESULT.SUBMITTED_AT.KEY,
                        hidden: filterOptions.some((item) => item === APPLICATION_CONFIRM_RESULT.SUBMITTED_AT.KEY),
                        render: () => <></>,
                    },
                ].filter((item) => !item.hidden),
            },
        ];
    }

    return (
        application?.line_items &&
        application.line_items.map((lineItem: App.ApplicationLineItem, index: number) => {
            return {
                title: lineItem.label,
                key: index,
                children: [
                    {
                        align: 'center',
                        title: (
                            <>
                                <span style={{ marginRight: '0.5em' }}>
                                    {APPLICATION_CONFIRM_RESULT.SCORE_GIVEN_AND_SCORE_MAXIMUM.TITLE}
                                </span>
                                <Tooltip title='複数回解答した場合、最新の得点・満点が表示されます'>
                                    <InfoCircleOutlined />
                                </Tooltip>
                            </>
                        ),
                        dataIndex: APPLICATION_CONFIRM_RESULT.SCORE_GIVEN_AND_SCORE_MAXIMUM.KEY,
                        hidden: filterOptions.some(
                            (item) => item === APPLICATION_CONFIRM_RESULT.SCORE_GIVEN_AND_SCORE_MAXIMUM.KEY
                        ),
                        render: (_: any, record: any) => {
                            if (!record?.lineItems?.[index]?.score_line_items) {
                                return <span>{`- / ${lineItem.score_maximum}`}</span>;
                            }

                            return (
                                <>
                                    <span>
                                        {`${record?.lineItems?.[index]?.score_line_items?.[0]?.score_given ?? '-'} / ${
                                            record?.lineItems?.[index]?.score_line_items?.[0]?.score_maximum ?? '-'
                                        }`}
                                    </span>
                                </>
                            );
                        },
                    },
                    {
                        title: APPLICATION_CONFIRM_RESULT.ACTIVITY_PROGRESS.TITLE,
                        dataIndex: APPLICATION_CONFIRM_RESULT.ACTIVITY_PROGRESS.KEY,
                        hidden: filterOptions.some((item) => item === APPLICATION_CONFIRM_RESULT.ACTIVITY_PROGRESS.KEY),
                        render: (_: any, record: any) => (
                            <ProgressStatusTag
                                status={record?.lineItems?.[index]?.score_line_items?.[0]?.activity_progress}
                            />
                        ),
                    },
                    {
                        title: APPLICATION_CONFIRM_RESULT.GRADING_PROGRESS.TITLE,
                        dataIndex: APPLICATION_CONFIRM_RESULT.GRADING_PROGRESS.KEY,
                        hidden: filterOptions.some((item) => item === APPLICATION_CONFIRM_RESULT.GRADING_PROGRESS.KEY),
                        render: (_: any, record: any) => (
                            <ScoringStatusTag
                                status={record?.lineItems?.[index]?.score_line_items?.[0]?.grading_progress}
                            />
                        ),
                    },
                    {
                        title: APPLICATION_CONFIRM_RESULT.TIMESTAMP.TITLE,
                        dataIndex: APPLICATION_CONFIRM_RESULT.TIMESTAMP.KEY,
                        hidden: filterOptions.some((item) => item === APPLICATION_CONFIRM_RESULT.TIMESTAMP.KEY),
                        render: (_: any, record: any) => {
                            const updatedAt = record?.lineItems?.[index]?.score_line_items?.[0]?.updated_at;
                            const createdAt = record?.lineItems?.[index]?.score_line_items?.[0]?.created_at;
                            if (updatedAt || createdAt) {
                                return (
                                    <span>
                                        {moment.unix(updatedAt ?? createdAt).format('YYYY/MM/DD HH:mm')}
                                    </span>
                                );
                            } else {
                                return '-';
                            }
                        },
                    },
                    {
                        title: APPLICATION_CONFIRM_RESULT.COMMENT.TITLE,
                        dataIndex: APPLICATION_CONFIRM_RESULT.COMMENT.KEY,
                        hidden: filterOptions.some((item) => item === APPLICATION_CONFIRM_RESULT.COMMENT.KEY),
                        render: (_: any, record: any) => {
                            if (record?.lineItems?.[index]?.score_line_items?.[0]?.comment) {
                                return (
                                    <span
                                        style={{
                                            width: '20ch',
                                            wordBreak: 'break-all',
                                            display: 'inline-block',
                                            overflowWrap: 'break-word',
                                        }}>
                                        {record.lineItems[index].score_line_items?.[0]?.comment}
                                    </span>
                                );
                            } else {
                                return '-';
                            }
                        },
                    },
                    {
                        title: APPLICATION_CONFIRM_RESULT.SCORING_USER_ID.TITLE,
                        dataIndex: APPLICATION_CONFIRM_RESULT.SCORING_USER_ID.KEY,
                        hidden: filterOptions.some((item) => item === APPLICATION_CONFIRM_RESULT.SCORING_USER_ID.KEY),
                        render: (_: any, record: any) => {
                            if (record?.lineItems?.[index]?.score_line_items?.[0]?.scoring_user_id) {
                                return (
                                    <span
                                        style={{
                                            width: '30ch',
                                            wordBreak: 'break-all',
                                            display: 'inline-block',
                                            overflowWrap: 'break-word',
                                        }}>
                                        {record.lineItems[index].score_line_items?.[0]?.scoring_user_id}
                                    </span>
                                );
                            } else {
                                return '-';
                            }
                        },
                    },
                    {
                        title: APPLICATION_CONFIRM_RESULT.STARTED_AT.TITLE,
                        dataIndex: APPLICATION_CONFIRM_RESULT.STARTED_AT.KEY,
                        hidden: filterOptions.some((item) => item === APPLICATION_CONFIRM_RESULT.STARTED_AT.KEY),
                        render: (_: any, record: any) => {
                            if (record?.lineItems?.[index]?.score_line_items?.[0]?.started_at) {
                                return (
                                    <span>
                                        {moment
                                            .unix(record.lineItems[index].score_line_items[0]?.started_at)
                                            .format('YYYY/MM/DD HH:mm')}
                                    </span>
                                );
                            } else {
                                return '-';
                            }
                        },
                    },
                    {
                        title: APPLICATION_CONFIRM_RESULT.SUBMITTED_AT.TITLE,
                        dataIndex: APPLICATION_CONFIRM_RESULT.SUBMITTED_AT.KEY,
                        hidden: filterOptions.some((item) => item === APPLICATION_CONFIRM_RESULT.SUBMITTED_AT.KEY),
                        render: (_: any, record: any) => {
                            if (record?.lineItems?.[index]?.score_line_items?.[0]?.submitted_at) {
                                return (
                                    <span>
                                        {moment
                                            .unix(record.lineItems[index].score_line_items[0]?.submitted_at)
                                            .format('YYYY/MM/DD HH:mm')}
                                    </span>
                                );
                            } else {
                                return '-';
                            }
                        },
                    },
                ].filter((item) => !item.hidden),
            };
        })
    );
};
